/**
 * AddSourceDialog — Add additional data sources to an existing workspace session.
 *
 * Reuses the same two wizard steps as NewTransformationWizard:
 *   Step 1 (WizardStepSources)  — pick files and/or connector tables (with column selection)
 *   Step 2 (WizardStepConfigure) — review columns / sheets per source
 *
 * Submit path differs from the wizard: files go through the agent session upload
 * endpoint so they land in the live DuckDB session immediately; connector tables
 * go through importFromConnector which already handles the session registration.
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Alert,
} from '@mui/material';
import { apiClient, buildUrl } from '../../services/apiClient';
import { validateCsvFiles } from '../../utils/csvValidation';
import { importFromConnector } from '../../services/connectorApi';
import WizardStepSources from '../dashboard/WizardStepSources';
import WizardStepConfigure from '../dashboard/WizardStepConfigure';
import type { ConnectorSourceEntry } from '../dashboard/ConnectorSourcePicker';

const STEPS = ['Select Sources', 'Review & Configure'];

/** Shape of the data returned by the session upload endpoint for each file. */
export interface UploadResult {
  tableName: string;
  rowCount: number;
  columnCount: number;
  replaced: boolean;
  schemaDiff?: {
    removed_columns?: string[];
    added_columns?: string[];
    type_changed?: Array<{ column: string; old_type: string; new_type: string }>;
    possible_renames?: Array<{ old: string; new: string }>;
  };
  affectedOperations?: Array<{ operation_id: string }>;
  brokenOperations?: Array<{ operation_id: string; broken_columns: string[] }>;
}

interface AddSourceDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** Agent session ID — required for both file uploads and connector imports. */
  sessionId: string | null;
  onSourceAdded?: () => void;
  /** Called with the API response data for each uploaded file so callers can send chat messages / set warnings. */
  onUploadsComplete?: (results: UploadResult[]) => void;
  /** If provided, pre-selects this connector on the Connect Source tab. */
  connectorId?: string;
  /** Initial tab: 0 = Upload Files, 1 = Connect Source. */
  initialTab?: 0 | 1;
  /**
   * When set, this dialog operates in "replace" mode: the uploaded file will
   * overwrite the named table instead of creating a new one.
   */
  replaceTable?: string | null;
  /**
   * All table names that belong to the same source group as replaceTable.
   * Used for Excel→CSV replace so the backend can drop all sheet tables.
   */
  replaceTableGroup?: string[];
}

export const AddSourceDialog: React.FC<AddSourceDialogProps> = ({
  open,
  onClose,
  projectId,
  sessionId,
  onSourceAdded,
  onUploadsComplete,
  replaceTable,
  replaceTableGroup,
}) => {
  const isReplaceMode = !!replaceTable;
  const [step, setStep] = useState(0);

  // Step 1 state — mirrors NewTransformationWizard
  const [files, setFiles] = useState<File[]>([]);
  const [connectorSources, setConnectorSources] = useState<ConnectorSourceEntry[]>([]);

  // Step 2 state
  const [columnSelections, setColumnSelections] = useState<Map<string, string[]>>(new Map());
  const [configureValid, setConfigureValid] = useState(false);

  // Submit state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalSources = files.length + connectorSources.length;
  const canProceedStep1 = totalSources > 0;

  const resetState = useCallback(() => {
    setStep(0);
    setFiles([]);
    setConnectorSources([]);
    setColumnSelections(new Map());
    setConfigureValid(false);
    setLoading(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (loading) return;
    resetState();
    onClose();
  }, [loading, resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const validationError = await validateCsvFiles(files);
      if (validationError) {
        setError(validationError);
        setLoading(false);
        return;
      }

      // ── Upload files through the session endpoint ──────────────────────────
      // This registers each file directly into the live DuckDB session so it
      // appears immediately in the source node.
      const uploadResults: UploadResult[] = [];
      for (const file of files) {
        // Keys: Excel sheets = file::<name>::<size>::<SheetName>, single files = file::<name>::<size>
        const fileBaseKey = `file::${file.name}::${file.size}`;
        const filePrefix = `${fileBaseKey}::`;
        const sheetEntries = Array.from(columnSelections.entries())
          .filter(([key]) => key.startsWith(filePrefix));
        const isMultiSheet = sheetEntries.length > 0;

        // Skip files where every sheet (or the single-sheet file itself) was deselected.
        if (isMultiSheet) {
          const hasAnySelectedSheet = sheetEntries.some(([, cols]) => cols.length > 0);
          if (!hasAnySelectedSheet) continue;
        } else {
          const singleCols = columnSelections.get(fileBaseKey);
          if (singleCols !== undefined && singleCols.length === 0) continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        if (isMultiSheet) {
          const selectedSheets = sheetEntries
            .filter(([, cols]) => cols.length > 0)
            .map(([key]) => key.slice(filePrefix.length));
          if (selectedSheets.length > 0) {
            formData.append('selected_sheets', JSON.stringify(selectedSheets));
          }
          const columnsBySheet: Record<string, string[]> = {};
          sheetEntries.forEach(([key, cols]) => {
            columnsBySheet[key.slice(filePrefix.length)] = cols;
          });
          if (Object.keys(columnsBySheet).length > 0) {
            formData.append('selected_columns_by_sheet', JSON.stringify(columnsBySheet));
          }
        } else {
          // Single file (CSV/SQL) — key is fileBaseKey
          const selectedCols = columnSelections.get(fileBaseKey);
          if (selectedCols && selectedCols.length > 0) {
            formData.append('selected_columns_by_sheet', JSON.stringify({ _file: selectedCols }));
          }
        }

        // In replace mode, tell the backend which table(s) to overwrite.
        if (isReplaceMode && replaceTable) {
          formData.append('replace_table', replaceTable);
          // If this source group had multiple tables (e.g. multi-sheet Excel),
          // send all of them so the backend can drop them before loading the new file.
          if (replaceTableGroup && replaceTableGroup.length > 1) {
            formData.append('replace_tables', JSON.stringify(replaceTableGroup));
          }
        }

        const resp = await apiClient.post(
          buildUrl(`/api/v1/agents/sessions/${sessionId}/upload`),
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 },
        );
        if (resp.data) {
          const d = resp.data;
          uploadResults.push({
            tableName: d.table_name,
            rowCount: d.row_count,
            columnCount: d.columns?.length ?? 0,
            replaced: !!d.replaced,
            schemaDiff: d.schema_diff,
            affectedOperations: d.affected_operations,
            brokenOperations: d.broken_operations,
          });
        }
      }

      // ── Import connector tables through the session endpoint ───────────────
      // Group by connectorId so we call importFromConnector once per connector.
      const byConnector = connectorSources.reduce<Record<string, ConnectorSourceEntry[]>>(
        (acc, entry) => { (acc[entry.connectorId] ??= []).push(entry); return acc; },
        {},
      );
      for (const [connectorId, entries] of Object.entries(byConnector)) {
        const tables = entries.map((e) => e.tableName);
        // Build columnsByTable from WizardStepConfigure selections.
        // For connector Excel entries, base key may have expanded into per-sheet keys.
        const columnsByTable: Record<string, string[]> = {};
        entries.forEach((e) => {
          const baseKey = e.key;
          const sheetPrefix = `${baseKey}::`;
          const sheetEntries = Array.from(columnSelections.entries())
            .filter(([k]) => k.startsWith(sheetPrefix));
          if (sheetEntries.length > 0) {
            sheetEntries.forEach(([sheetKey, cols]) => {
              columnsByTable[sheetKey.slice(sheetPrefix.length)] = cols;
            });
          } else {
            const cols = columnSelections.get(baseKey);
            if (cols && cols.length > 0) columnsByTable[e.tableName] = cols;
          }
        });
        const result = await importFromConnector(
          sessionId,
          connectorId,
          tables,
          Object.keys(columnsByTable).length > 0 ? columnsByTable : undefined,
          isReplaceMode && replaceTable ? replaceTable : undefined,
          isReplaceMode && replaceTableGroup && replaceTableGroup.length > 0 ? replaceTableGroup : undefined,
        );
        // Surface connector replace as an upload result so the chat message fires
        if (result && isReplaceMode) {
          result.tables.forEach((t) => {
            uploadResults.push({
              tableName: t.table_name,
              rowCount: t.row_count,
              columnCount: t.columns?.length ?? 0,
              replaced: true,
            });
          });
        }
      }

      if (uploadResults.length > 0) onUploadsComplete?.(uploadResults);
      onSourceAdded?.();
      resetState();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  }, [sessionId, files, connectorSources, columnSelections, isReplaceMode, replaceTable, onUploadsComplete, onSourceAdded, resetState, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isReplaceMode ? `Replace "${replaceTable}"` : 'Add Data Source'}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {step === 0 && (
          <WizardStepSources
            projectId={projectId}
            files={files}
            connectorSources={connectorSources}
            onFilesChange={setFiles}
            onConnectorSourcesChange={setConnectorSources}
          />
        )}

        {step === 1 && (
          <WizardStepConfigure
            files={files}
            connectorSources={connectorSources}
            columnSelections={columnSelections}
            onColumnSelectionsChange={setColumnSelections}
            onValidityChange={setConfigureValid}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>

        {step === 0 && (
          <Button variant="contained" onClick={() => setStep(1)} disabled={!canProceedStep1}>
            Next
          </Button>
        )}

        {step === 1 && (
          <>
            <Button onClick={() => setStep(0)} disabled={loading}>Back</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!configureValid || loading}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              {loading
                ? (isReplaceMode ? 'Replacing...' : 'Adding...')
                : (isReplaceMode ? 'Replace' : `Add ${totalSources} source(s)`)}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddSourceDialog;
